<template>
    <div class="page">
        <kt-datatable
            :model="notificationModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: '-table-bordered', rowClass: (row) => getRowClass(row) }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            storage-key="notifications-list"
            @cell-click="handleCellClick"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>
            
            <template #view_actions="scope">
                <div class="btn-group btn-group-sm">
                    <button
                        class="btn btn-outline-primary btn-sm"
                        @click.stop="viewNotification(scope.row)"
                        title="View"
                    >
                        <Eye :size="14" />
                    </button>
                    <button
                        v-if="!scope.row.IsRead"
                        class="btn btn-outline-success btn-sm"
                        @click.stop="markAsRead(scope.row.Id)"
                        title="Mark as Read"
                    >
                        <Check :size="14" />
                    </button>
                    <button
                        class="btn btn-outline-danger btn-sm"
                        @click.stop="deleteNotification(scope.row.Id)"
                        title="Delete"
                    >
                        <Trash2 :size="14" />
                    </button>
                </div>
            </template>

            <template #filters="scope">
                <div class="col">
                    <div class="row">
                        <div class="col-auto">
                            <kt-autoselect
                                label="Mailbox"
                                :options="mailboxOptions"
                                display-by="label"
                                value-by="value"
                                :multiple="true"
                                :model-value="currentFilter.mailbox"
                                @update:model-value="(value: string[]) => setFilter({ mailbox: value })"
                                search-placeholder="Search mailbox..."
                            />
                        </div>
                        <div class="col-auto">
                            <kt-autoselect
                                label="Status"
                                :options="statusOptions"
                                display-by="label"
                                value-by="value"
                                :multiple="true"
                                :model-value="currentFilter.status"
                                @update:model-value="(value: string[]) => setFilter({ status: value })"
                                search-placeholder="Search status..."
                            />
                        </div>
                    </div>
                </div>
            </template>

            <template #after-search="scope">
                <div class="-btn-group">
                    <button @click="refresh()" class="btn btn-sm btn-outline-secondary me-2">
                        Refresh <i class="fa fa-sync"></i>
                    </button>
                    <button @click="markAllAsRead()" class="btn btn-sm btn-outline-success me-2">
                        Mark All Read <Check :size="14" />
                    </button>
                    <button @click="openNewMessage()" class="btn btn-sm btn-outline-primary">
                        New Message <i class="fa fa-plus"></i>
                    </button>
                </div>
            </template>

            <template #under-header>
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            class="btn btn-sm btn-outline-success"
                            @click="markSelectedAsRead"
                        >
                            <Check :size="14" class="me-1" />
                            Mark Read
                        </button>
                        <button 
                            class="btn btn-sm btn-outline-danger"
                            @click="deleteSelected"
                        >
                            <Trash2 :size="14" class="me-1" />
                            Delete
                        </button>
                        <button 
                            class="btn btn-sm btn-outline-secondary ms-auto"
                            @click="clearSelection"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>

                <!-- Filter Badges -->
                <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>

                    <span v-if="currentFilter.mailbox.length > 0" class="badge bg-primary d-flex align-items-center">
                        Mailbox: {{ currentFilter.mailbox.map((v: string) => mailboxOptions.find(o => o.value === v)?.label).join(', ') }}
                        <button type="button" class="btn-close btn-close-white ms-2"
                                @click="setFilter({ mailbox: [] })"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <span v-if="currentFilter.status.length > 0" class="badge bg-info d-flex align-items-center">
                        Status: {{ currentFilter.status.map((v: string) => statusOptions.find(o => o.value === v)?.label).join(', ') }}
                        <button type="button" class="btn-close btn-close-white ms-2"
                                @click="setFilter({ status: [] })"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <span v-if="currentFilter.searchTerm" class="badge bg-secondary d-flex align-items-center">
                        Search: {{ currentFilter.searchTerm }}
                        <button type="button" class="btn-close btn-close-white ms-2"
                                @click="setFilter({ searchTerm: '' })"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <button class="btn btn-sm btn-outline-secondary ms-0"
                            @click="clearAllFilters()">
                        Clear All
                    </button>
                </div>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'Subject'">
                    <span v-if="!scope.row.IsRead" class="badge bg-primary me-2">New</span>
                    <strong :class="{ 'fw-bold': !scope.row.IsRead }">{{ scope.row.Subject || '(No Subject)' }}</strong>
                </span>
                <span v-else-if="scope.column.name === 'NotificationType'">
                    <span :class="getTypeBadgeClass(scope.row.NotificationType)" class="badge">
                        {{ scope.row.NotificationType }}
                    </span>
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
        </kt-datatable>

        <!-- View Notification Modal -->
        <kt-modal v-model:visible="showViewModal" @dismiss="showViewModal = false;" title="Notification" size="lg">
            <template #body>
                <div v-if="selectedNotification">
                    <div class="mb-3">
                        <label class="form-label fw-bold">From</label>
                        <p>{{ selectedNotification.SenderName || 'System' }}</p>
                    </div>
                    <div class="mb-3">
                        <label class="form-label fw-bold">To</label>
                        <p>{{ selectedNotification.RecipientName || '—' }}</p>
                    </div>
                    <div class="mb-3">
                        <label class="form-label fw-bold">Subject</label>
                        <p>{{ selectedNotification.Subject || '(No Subject)' }}</p>
                    </div>
                    <div class="mb-3">
                        <label class="form-label fw-bold">Type</label>
                        <p>
                            <span :class="getTypeBadgeClass(selectedNotification.NotificationType)" class="badge">
                                {{ selectedNotification.NotificationType }}
                            </span>
                        </p>
                    </div>
                    <div class="mb-3">
                        <label class="form-label fw-bold">Date</label>
                        <p>{{ formatDate(selectedNotification.CreatedDate) }}</p>
                    </div>
                    <div class="mb-3">
                        <label class="form-label fw-bold">Message</label>
                        <div class="border rounded p-3 bg-light" v-html="selectedNotification.Body"></div>
                    </div>
                    <div v-if="selectedNotification.RelatedEntityType && selectedNotification.RelatedEntityId" class="mb-3">
                        <button 
                            class="btn btn-primary"
                            @click="navigateToRelated(selectedNotification)"
                        >
                            View {{ selectedNotification.RelatedEntityType }}
                        </button>
                    </div>
                </div>
            </template>
            <template #footer>
                <button class="btn btn-secondary" @click="showViewModal = false">Close</button>
            </template>
        </kt-modal>

        <!-- New Message Modal -->
        <kt-modal v-model:visible="showNewMessageModal" title="New Message" size="lg">
            <template #body>
                <div class="mb-3">
                    <label class="form-label">Recipient</label>
                    <tech-ac
                        v-model="newMessage.recipientTechId"
                        label=""
                        placeholder="Select a technician..."
                        :active-only="true"
                    />
                </div>
                <div class="mb-3">
                    <label class="form-label">Subject</label>
                    <input type="text" class="form-control form-control-sm" placeholder="Message Subject" v-model="newMessage.subject" />
                </div>
                <div class="mb-3">
                    <label class="form-label">Message</label>
                    <textarea class="form-control form-control-sm" placeholder="Message Body" rows="6" v-model="newMessage.body"></textarea>
                </div>
            </template>
            <template #footer>
                <button class="btn btn-secondary" @click="showNewMessageModal = false">Cancel</button>
                <button class="btn btn-primary" @click="sendMessage" :disabled="isSending">
                    <span v-if="isSending">Sending...</span>
                    <span v-else>Send Message</span>
                </button>
            </template>
        </kt-modal>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import KtAutoselect from "@/components/UI/KtAutoselect.vue";
import KtModal from "@/components/UI/KtModal.vue";
import TechAc from "@/components/Techs/TechAc.vue";
import { Eye, Check, Trash2 } from 'lucide-vue-next';
import ApiService from "@/services/ApiService";
import Cache from "@/services/Cache";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import { usePermissions } from '@/composables';

const router = useRouter();
const route = useRoute();
const { isAdmin } = usePermissions();

// ===== FILTER OPTIONS =====

const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Unread', value: 'unread' },
    { label: 'Read', value: 'read' },
    { label: 'Deleted', value: 'deleted' }
];

const mailboxOptions = [
    { label: 'All Messages', value: 'all' },
    { label: 'Inbox', value: 'inbox' },
    { label: 'Sent', value: 'sent' }
];

// ===== FILTER STATE =====

interface NotificationListFilter {
    mailbox: string[];
    status: string[];
    searchTerm: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

const getDefaultFilter = (): NotificationListFilter => ({
    mailbox: [],
    status: [],
    searchTerm: '',
    page: 0,
    size: 25,
    sort: []
});

const currentFilter = ref<NotificationListFilter>(getDefaultFilter());

// Datatable filter (derived from currentFilter — KtDatatable reads this)
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: currentFilter.value.searchTerm,
    sort: currentFilter.value.sort
}));

const total = ref(0);
const rows = ref<any[]>([]);

// ===== UI STATE =====

const selectedRows = ref<number[]>([]);
const selectAll = ref(false);
const showViewModal = ref(false);
const showNewMessageModal = ref(false);
const selectedNotification = ref<any>(null);
const isSending = ref(false);

const newMessage = ref({
    recipientTechId: null as number | null,
    subject: '',
    body: ''
});

// ===== DATATABLE MODEL =====

const notificationModel = [
    {
        name: "Select",
        label: "",
        type: "select",
        width: 40,
        locked: true,
        draggable: false
    },
    {
        name: "Id",
        label: "ID",
        display: false
    },
    {
        name: "Subject",
        label: "Subject",
        display: true,
        type: "string",
        sortable: true
    },
    {
        name: "SenderName",
        label: "From",
        display: true,
        type: "string",
        width: 150
    },
    {
        name: "RecipientName",
        label: "To",
        display: true,
        type: "string",
        width: 150
    },
    {
        name: "NotificationType",
        label: "Type",
        display: true,
        type: "string",
        width: 120
    },
    {
        name: "CreatedDate",
        label: "Date",
        display: true,
        type: "date",
        sortable: true,
        width: 150
    },
    {
        name: "IsRead",
        label: "Read",
        display: false
    },
    {
        name: "Actions",
        label: "Actions",
        type: "actions",
        width: 120,
        locked: true
    }
];

// ===== HELPERS =====

const getRowClass = (row: any) => {
    if (row.IsDeleted) return 'row-deleted';
    return row.IsRead ? '' : 'table-info';
};

const getTypeBadgeClass = (type: string) => {
    switch (type) {
        case 'Message': return 'bg-primary';
        case 'Update': return 'bg-info';
        case 'Alert': return 'bg-warning';
        case 'System': return 'bg-secondary';
        default: return 'bg-secondary';
    }
};

const formatDate = (date: string) => {
    if (!date) return '';
    return new Date(date).toLocaleString();
};

// ===== URL & FILTER UTILITIES =====

const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            return { [trimmed.substring(1)]: 'DESC' as const };
        }
        return { [trimmed]: 'ASC' as const };
    });
};

const parseUrlToFilter = (): NotificationListFilter => {
    const q = route.query;
    return {
        mailbox: q.mailbox ? (q.mailbox as string).split(',') : [],
        status: q.status ? (q.status as string).split(',') : [],
        searchTerm: (q.searchTerm as string) || '',
        page: parseInt((q.page as string) || '0'),
        size: parseInt((q.size as string) || '25'),
        sort: parseSortFromUrl(q.sort as string)
    };
};

const buildUrlFromFilter = (filter: NotificationListFilter): string => {
    const params = new URLSearchParams();

    if (filter.mailbox && filter.mailbox.length > 0) params.set('mailbox', filter.mailbox.join(','));
    if (filter.status && filter.status.length > 0) params.set('status', filter.status.join(','));
    if (filter.searchTerm) params.set('searchTerm', filter.searchTerm);

    if (filter.sort && filter.sort.length > 0) {
        const sortString = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        }).join(',');
        params.set('sort', sortString);
    }

    params.set('page', filter.page.toString());
    if (filter.size !== 25) params.set('size', filter.size.toString());

    return params.toString();
};

const buildApiParams = (filter: NotificationListFilter) => {
    const params: any = {
        page: filter.page + 1,
        pageSize: filter.size
    };

    // Mailbox filter — use first selected value or 'all'
    if (filter.mailbox && filter.mailbox.length > 0) {
        params.mailbox = filter.mailbox.length === 1 ? filter.mailbox[0] : filter.mailbox.join(',');
    } else {
        params.mailbox = 'all';
    }

    // Status filter — support multiple selections
    const statuses = filter.status || [];
    if (statuses.includes('unread')) {
        params.isRead = false;
    } else if (statuses.includes('read')) {
        params.isRead = true;
    }
    if (statuses.includes('deleted')) {
        params.deletedOnly = true;
    }

    if (filter.searchTerm) {
        params.searchTerm = filter.searchTerm;
    }

    if (filter.sort && filter.sort.length > 0) {
        const orderByParts = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        });
        params.orderBy = orderByParts.join(',');
    }

    return params;
};

// ===== API INTEGRATION =====

const loadData = async () => {
    try {
        const params = buildApiParams(currentFilter.value);
        console.debug('Notification API params:', params);

        const response = await ApiService.getNotifications(params);

        if (response.Status === 1) {
            rows.value = response.Data || [];
            total.value = response.Meta?.TotalCount || 0;
        }
    } catch (error) {
        console.error('Error loading notifications:', error);
        Toast.error('Failed to load notifications');
    }
};

// ===== FILTER UPDATE HANDLERS =====

const handleDatatableUpdate = (filterUpdate: any) => {
    console.debug('Datatable filter update:', filterUpdate);

    if (filterUpdate.page !== undefined) currentFilter.value.page = filterUpdate.page;
    if (filterUpdate.size !== undefined) currentFilter.value.size = filterUpdate.size;
    if (filterUpdate.sort !== undefined) currentFilter.value.sort = filterUpdate.sort || [];
    if (filterUpdate.search !== undefined) currentFilter.value.searchTerm = filterUpdate.search;

    // Save to cache
    Cache.set('notification-list-filter', currentFilter.value);

    // Update URL
    const urlParams = buildUrlFromFilter(currentFilter.value);
    const newUrl = `/notification/list${urlParams ? '?' + urlParams : ''}`;
    router.replace(newUrl);

    refresh();
};

const setFilter = (filterData: Partial<NotificationListFilter>) => {
    console.debug("setFilter:", filterData);

    const tempFilter = { ...currentFilter.value };

    if (filterData.mailbox !== undefined) tempFilter.mailbox = Array.isArray(filterData.mailbox) ? filterData.mailbox : (filterData.mailbox ? [filterData.mailbox] : []);
    if (filterData.status !== undefined) tempFilter.status = Array.isArray(filterData.status) ? filterData.status : (filterData.status ? [filterData.status] : []);
    if (filterData.searchTerm !== undefined) tempFilter.searchTerm = filterData.searchTerm;
    if (filterData.sort !== undefined) tempFilter.sort = filterData.sort || [];

    // Reset to first page when filter changes
    tempFilter.page = 0;

    // 1. Update currentFilter
    currentFilter.value = tempFilter;

    // 2. Save to cache
    Cache.set('notification-list-filter', currentFilter.value);

    // 3. Update URL
    const urlParams = buildUrlFromFilter(tempFilter);
    const newUrl = `/notification/list${urlParams ? '?' + urlParams : ''}`;
    router.replace(newUrl);

    // 4. Reload data
    refresh();
};

const hasActiveFilters = computed(() => {
    return currentFilter.value.mailbox.length > 0 ||
           currentFilter.value.status.length > 0 ||
           currentFilter.value.searchTerm !== '';
});

const clearAllFilters = () => {
    setFilter(getDefaultFilter());
};

// ===== INITIALIZATION =====

const hasUrlParams = (): boolean => {
    const q = route.query;
    return !!(q.mailbox || q.status || q.searchTerm || q.sort);
};

const hasNonEmptyFilterValues = (filters: any): boolean => {
    if (!filters) return false;
    if (filters.mailbox && filters.mailbox.length > 0) return true;
    if (filters.status && filters.status.length > 0) return true;
    if (filters.searchTerm) return true;
    return false;
};

const initializeFilter = async (): Promise<void> => {
    if (hasUrlParams()) {
        // URL has params — use those
        currentFilter.value = parseUrlToFilter();
        Cache.set('notification-list-filter', currentFilter.value);
        console.debug('Initialized from URL params:', currentFilter.value);
    } else {
        // Blank URL — try cached filters
        const stored = Cache.get('notification-list-filter');
        if (stored && hasNonEmptyFilterValues(stored)) {
            currentFilter.value = {
                ...getDefaultFilter(),
                ...stored,
                page: 0
            };
            console.debug('Initialized from cache:', currentFilter.value);
        } else {
            currentFilter.value = getDefaultFilter();
            console.debug('Initialized with defaults');
        }

        // Update URL to reflect current state
        const urlParams = buildUrlFromFilter(currentFilter.value);
        const newUrl = `/notification/list${urlParams ? '?' + urlParams : ''}`;
        router.replace(newUrl);
    }
};

// ===== ACTIONS =====

const refresh = () => {
    loadData();
    Emitter.emit('notification:refresh');
};

const handleCellClick = (row: any, column: any) => {
    if (column.name !== 'Select' && column.name !== 'Actions') {
        viewNotification(row);
    }
};

const viewNotification = async (notification: any) => {
    selectedNotification.value = notification;
    showViewModal.value = true;

    if (!notification.IsRead) {
        await markAsRead(notification.Id);
        notification.IsRead = true;
    }
};

const markAsRead = async (id: number) => {
    try {
        await ApiService.markNotificationRead(id);
        const notification = rows.value.find(n => n.Id === id);
        if (notification) {
            notification.IsRead = true;
        }
        Emitter.emit('notification:refresh');
    } catch (error) {
        console.error('Error marking as read:', error);
    }
};

const markAllAsRead = async () => {
    try {
        await ApiService.markAllNotificationsRead();
        rows.value.forEach(n => n.IsRead = true);
        Emitter.emit('notification:refresh');
    } catch (error) {
        Toast.error('Failed to mark all as read');
    }
};

const markSelectedAsRead = async () => {
    for (const id of selectedRows.value) {
        await markAsRead(id);
    }
    clearSelection();
};

const deleteNotification = async (id: number) => {
    Alert.confirm('Are you sure you want to delete this notification?', async () => {
        try {
            await ApiService.deleteNotification(id);
            rows.value = rows.value.filter(n => n.Id !== id);
            Emitter.emit('notification:refresh');
        } catch (error) {
            Toast.error('Failed to delete notification');
        }
    }, () => {});
};

const deleteSelected = async () => {
    Alert.confirm(`Delete ${selectedRows.value.length} notifications?`, async () => {
        try {
            await ApiService.deleteMultipleNotifications(selectedRows.value);
            rows.value = rows.value.filter(n => !selectedRows.value.includes(n.Id));
            clearSelection();
            Emitter.emit('notification:refresh');
        } catch (error) {
            Toast.error('Failed to delete notifications');
        }
    }, () => {});
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
    } else {
        selectedRows.value = rows.value.map(r => r.Id);
    }
    selectAll.value = !selectAll.value;
};

const toggleRowSelection = (id: number) => {
    const index = selectedRows.value.indexOf(id);
    if (index > -1) {
        selectedRows.value.splice(index, 1);
    } else {
        selectedRows.value.push(id);
    }
};

const clearSelection = () => {
    selectedRows.value = [];
    selectAll.value = false;
};

const openNewMessage = () => {
    newMessage.value = {
        recipientTechId: null,
        subject: '',
        body: ''
    };
    showNewMessageModal.value = true;
};

const sendMessage = async () => {
    if (!newMessage.value.recipientTechId) {
        Toast.error('Please select a recipient');
        return;
    }

    isSending.value = true;
    try {
        await ApiService.saveNotification({
            RecipientUserId: newMessage.value.recipientTechId,
            Subject: newMessage.value.subject,
            Body: newMessage.value.body,
            NotificationType: 'Message'
        });
        showNewMessageModal.value = false;
        refresh();
    } catch (error) {
        Toast.error('Failed to send message');
    } finally {
        isSending.value = false;
    }
};

const navigateToRelated = (notification: any) => {
    const type = notification.RelatedEntityType?.toLowerCase();
    const id = notification.RelatedEntityId;

    showViewModal.value = false;

    switch (type) {
        case 'job':
        case 'workorder':
            Emitter.emit('edit:job', id);
            break;
        case 'task':
        case 'time':
            Emitter.emit('open:taskForm', id);
            break;
        case 'request':
            router.push(`/request/edit/${id}`);
            break;
        default:
            console.log('Unknown entity type:', type);
    }
};

// ===== LIFECYCLE =====

onMounted(async () => {
    await initializeFilter();
    loadData();
});
</script>

<style scoped>
.actions-row {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

:deep(.row-deleted td) {
    text-decoration: line-through;
    opacity: 0.6;
}
</style>
